import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { type ReactNode, useCallback } from 'react'
import { useSelector } from 'react-redux'

import { ATTENTION_STATUS_ACTIONS } from 'partner360/entities/attentionStatuses'
import { useAcknowledgmentPendingSigners } from 'partner360/features/acknowledgmentStatusDialog'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { invalidateFinancialCockpitData } from '../../../../model/financialCockpitSlice'
import { selectEffectiveCockpitFYear } from '../../../../model/selectors'
import type { IAttentionStatusesItem } from '../../types'

import {
  AttentionNextStepRow,
  type IAttentionNextStepExtraAction,
  type IAttentionNextStepMark,
  type TAttentionNextStepTone,
} from './AttentionNextStepRow'
import { useCockpitPlanSignerRows } from './useCockpitPlanSignerRows'
import { usePlanSignFlow } from './usePlanSignFlow'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.nextSteps

interface IStepAct {
  tone: TAttentionNextStepTone
  labelKey?: string
  shortLabelKey?: string
  icon?: string
  loading?: boolean
  disabled?: boolean
  extraAction?: IAttentionNextStepExtraAction
  onClick: () => void
}

interface IManagerAcknowledgmentStepProps {
  partnerId: string
  /** The offering whose plan is signed; without one the signers cannot be read. */
  offering: string | null
  mark: IAttentionNextStepMark
  name: ReactNode
  fact: ReactNode
  item: IAttentionStatusesItem
  onSelect: (id: string) => void
  /** Opens the partner's plan in a new tab beside the signature or the reminder; the PBM has none. */
  onOpenPlan?: () => void
}

/**
 * A partner's `not fully acknowledged` step on the manager cockpit, decided by the plan's own
 * signers as the partner's cockpit decides it: the reader holding a row to sign signs it after the
 * plan review; every other reader reminds the first signer the plan waits for, once a day; a
 * reader allowed neither opens the status' detail.
 */
export const ManagerAcknowledgmentStep = ({
  partnerId,
  offering,
  mark,
  name,
  fact,
  item,
  onSelect,
  onOpenPlan,
}: IManagerAcknowledgmentStepProps) => {
  const dispatch = useAppDispatch()
  const fYear = useSelector(selectEffectiveCockpitFYear)

  const handleUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  const knownItems = useCockpitPlanSignerRows(partnerId, offering)
  const { signers, hasLoaded, ownRowToSign, isReminded, remind, isSubmitting } =
    useAcknowledgmentPendingSigners({
      partnerId,
      fYear,
      offering,
      knownItems,
      onUpdated: handleUpdated,
    })

  const signFlow = usePlanSignFlow({ partnerId, fYear, offering })
  const openDetail = () => onSelect(item.id)

  const signer = signers.find(({ CanResend }) => CanResend > 0)
  const signerName = signer ? signer.SignerName?.trim() || signer.SignerEmail || '' : ''
  const reminded = signer ? isReminded(signer) : false
  const openPlanAction: IAttentionNextStepExtraAction | undefined = onOpenPlan && {
    icon: 'kbase kbase-external-link',
    labelKey: KEYS.openPlan,
    shortLabelKey: KEYS.short.plan,
    onClick: onOpenPlan,
  }

  const seeDetails: IStepAct = {
    tone: 'go',
    labelKey: KEYS.seeDetails,
    shortLabelKey: KEYS.short.details,
    icon: 'kbase kbase-chevron-right',
    onClick: openDetail,
  }

  const getStep = (): IStepAct => {
    if (!offering) {
      return seeDetails
    }

    /* While the signers are read the button waits, so the step does not first offer the wrong
       act. */
    if (!hasLoaded) {
      return {
        tone: 'go',
        labelKey: ATTENTION_STATUS_ACTIONS.acknowledge.labelKey,
        shortLabelKey: KEYS.short.sign,
        icon: 'kbase kbase-check',
        loading: true,
        disabled: true,
        onClick: () => undefined,
      }
    }

    if (ownRowToSign) {
      return {
        tone: 'sign',
        labelKey: ATTENTION_STATUS_ACTIONS.acknowledge.labelKey,
        shortLabelKey: KEYS.short.sign,
        loading: signFlow.isSigning,
        extraAction: openPlanAction,
        onClick: () => signFlow.start(ownRowToSign.EntryId),
      }
    }

    if (signer) {
      return {
        tone: 'remind',
        labelKey: reminded ? KEYS.remindedToday : KEYS.remind,
        shortLabelKey: KEYS.short.remind,
        disabled: reminded || isSubmitting,
        extraAction: openPlanAction,
        onClick: () => void remind(signer),
      }
    }

    return seeDetails
  }

  const step = getStep()

  /* The flow stays mounted whatever the row offers, so a signature under way is not cut off once
     the rows are read back signed. */
  return (
    <>
      <AttentionNextStepRow
        mark={mark}
        name={name}
        fact={
          step.tone === 'remind' && signerName ? (
            <>
              {fact}
              {` · ${signerName}`}
            </>
          ) : (
            fact
          )
        }
        severity={item.severity}
        {...step}
      />
      {signFlow.element}
    </>
  )
}

ManagerAcknowledgmentStep.displayName = 'ManagerAcknowledgmentStep'
